import React, { useEffect, useRef, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { signup, sendOtp, verifyOtp } from "../services/authApi";
import { useAuth } from "../context/AuthContext";

interface LoginPopupProps {
  onClose: () => void;
   onLoginSuccess: (name: string) => void;
}

const isEmail = (val: string) => /^\S+@\S+\.\S+$/.test(val.trim());
const isPhone = (val: string) => {
  const digits = val.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

const LoginPopup: React.FC<LoginPopupProps> = ({ onClose, onLoginSuccess }) => {
  const [isLoginView, setIsLoginView] = useState(true);
  const { login } = useAuth();

  // Signup fields
  const [signupName, setSignupName] = useState("");
  const [signupMobile, setSignupMobile] = useState("");
  const [signupEmail, setSignupEmail] = useState("");

  // Login field
  const [loginIdentifier, setLoginIdentifier] = useState("");

  // OTP state
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Resend timer
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = window.setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resendTimer]);

  const handleToggleView = () => {
    setError(null);
    setMessage(null);
    setIsLoginView((prev) => !prev);
    // reset otp flows
    setShowOtp(false);
    setOtp("");
    setOtpSentTo(null);
  };

  // -------- SIGNUP ----------
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!signupName.trim()) return setError("Please enter your name");
    if (!isPhone(signupMobile)) return setError("Please enter a valid mobile number");
    if (!isEmail(signupEmail)) return setError("Please enter a valid email address");

    try {
      await signup({ name: signupName.trim(), mobile: signupMobile.trim(), email: signupEmail.trim() });
      setMessage("Registration successful. You can now login.");
      // reset signup form
      setSignupName("");
      setSignupMobile("");
      setSignupEmail("");
      // switch to login view after small delay so user sees message
      setTimeout(() => {
        setIsLoginView(true);
        setMessage(null);
      }, 1000);
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data || err.message || "Signup failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  // -------- LOGIN (send OTP) ----------
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const val = loginIdentifier.trim();
    if (!val) return setError("Please enter email or mobile number");
    if (!isEmail(val) && !isPhone(val)) return setError("Enter valid email or phone");

    try {
      const resp = await sendOtp(val);
      setMessage("OTP sent. Check your email (or WhatsApp when configured).");
      setOtpSentTo(val);
      setShowOtp(true);
      setResendTimer(60); // 60s cooldown
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 404 || (typeof data === "string" && data.toLowerCase().includes("register"))) {
        setError("User not found — please register first.");
      } else {
        setError(data || err.message || "Failed to send OTP");
      }
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!otpSentTo) return setError("No OTP request in progress");
    if (!otp.trim()) return setError("Please enter OTP");

try {
      const response = await verifyOtp(otpSentTo, otp.trim());
      // Expecting backend to return { name, email, token }
      const { name, email, token } = response.data;
      
      setMessage("OTP verified — login successful!");
      
      // Use Context to Login (Saves to LocalStorage)
      login(token, { name, email });

      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err: any) {
      console.error(err);
      const data = err?.response?.data;
      setError(data || err.message || "Invalid or expired OTP");
    }
  };

  const handleResend = async () => {
    if (!otpSentTo) return;
    if (resendTimer > 0) return;
    try {
      await sendOtp(otpSentTo);
      setMessage("OTP resent.");
      setResendTimer(60);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data || "Failed to resend OTP");
    }
  };

  // -------- Google Login / Signup ----------
  const openGoogleAuth = (forSignup = false) => {
  // window.location.href = "http://localhost:2025/oauth2/authorization/google";
  window.location.href = "https://www.projectinfotech.in/oauth2/authorization/google";
  };

  // ---------- Watermark (SVG) ----------
  const Watermark = () => (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 400 200"
        className="w-80 h-80 opacity-10"
        fill="none"
      >
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="font-extrabold" fontSize="90" fill="white">
          PIT
        </text>
        <text x="20" y="110" fontSize="70" fill="white">{`{`}</text>
        <text x="340" y="110" fontSize="70" fill="white">{`}`}</text>
        <circle cx="60" cy="50" r="18" stroke="white" strokeWidth="4" />
        <path d="M60 38v6M60 56v6M48 50h6M66 50h6" stroke="white" strokeWidth="3" strokeLinecap="round" />
        <line x1="100" y1="150" x2="300" y2="150" stroke="white" strokeWidth="3" />
        <circle cx="100" cy="150" r="5" fill="white" />
        <circle cx="300" cy="150" r="5" fill="white" />
      </svg>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm animate-fade-in-backdrop">
      <div className="relative max-w-2xl w-full mx-4 rounded-lg shadow-2xl overflow-hidden bg-white">
        {/* Sliding Container */}
        <div className="relative h-full w-full overflow-hidden" style={{ minHeight: "460px" }}>
          <div
            className={`absolute top-0 left-0 w-[200%] h-full flex transition-transform duration-700 ease-in-out
              ${isLoginView ? "translate-x-0" : "-translate-x-1/2"}`}
          >
            {/* LOGIN VIEW */}
            <div className="w-1/2 flex">
              {/* Red Panel */}
              <div
                className={`hidden sm:flex relative flex-col justify-center p-8 bg-red-600 text-white w-1/2 transition-all duration-700
                ${isLoginView ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
              >
                <Watermark />

                <h2 className="text-3xl font-aligran font-bold mb-2 relative z-10">Login to Account</h2>
                <p className="text-sm opacity-90 mb-4 relative z-10">Stay connected and get full access.</p>
                <div className="mt-auto relative z-10">
                  <p className="text-sm opacity-90">
                    Don’t have an account?
                    <button onClick={handleToggleView} className="font-semibold underline ml-1">
                      Create an account
                    </button>
                  </p>
                </div>
              </div>

              {/* White Panel - Login */}
              <div
                className={`relative bg-white w-full sm:w-1/2 transition-all duration-700 flex flex-col justify-center
                ${isLoginView ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
              >
                <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-gray-800 text-3xl">
                  &times;
                </button>

                <div className="px-8 py-6">
                  <h2 className="text-2xl font-aligran font-bold text-gray-900 mb-6 text-center">User Login</h2>

                  {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
                  {message && <div className="mb-3 text-sm text-green-600">{message}</div>}

                  {!showOtp && (
                    <form className="space-y-4" onSubmit={handleLoginSubmit}>
                      <input
                        type="text"
                        placeholder="Enter Email or Mobile"
                        value={loginIdentifier}
                        onChange={(e) => setLoginIdentifier(e.target.value)}
                        className="w-full p-3 border border-gray-400 rounded-md placeholder-gray-600 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />

                      <button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md">
                        Send OTP
                      </button>

                      <button
                        type="button"
                        onClick={() => openGoogleAuth(true)}
                        className="w-full flex items-center justify-center gap-2 border border-gray-400 py-2 px-4 rounded-md hover:bg-gray-100 transition text-gray-800 font-medium"
                      >
                        <FcGoogle size={22} /> Login with Google
                      </button>
                    </form>
                  )}

                  {showOtp && (
                    <form className="space-y-4" onSubmit={handleVerify}>
                      <div className="text-sm text-gray-700">OTP sent to: <strong>{otpSentTo}</strong></div>

                      <input
                        type="text"
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="w-full p-3 border border-gray-400 rounded-md placeholder-gray-600 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />

                      <div className="flex gap-2">
                        <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md">
                          Verify OTP
                        </button>

                        <button
                          type="button"
                          onClick={handleResend}
                          disabled={resendTimer > 0}
                          className={`px-4 py-2 rounded-md border ${resendTimer > 0 ? "text-gray-400 border-gray-300" : "text-gray-800 border-gray-400"} bg-white`}
                        >
                          {resendTimer > 0 ? `Resend (${resendTimer}s)` : "Resend"}
                        </button>
                      </div>

                      <div className="text-xs text-gray-500">
                        If you didn't receive the code, check spam or try resending. For WhatsApp OTP setup, enable number in admin.
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* SIGNUP VIEW */}
            <div className="w-1/2 flex">
              {/* Red Panel */}
              <div
                className={`hidden sm:flex relative flex-col justify-center p-8 bg-red-600 text-white w-1/2 transition-all duration-700
                ${!isLoginView ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
              >
                <Watermark />

                <h2 className="text-3xl font-aligran font-bold mb-2 relative z-10">Create an Account</h2>
                <p className="text-sm opacity-90 mb-4 relative z-10">Join our community today.</p>
                <div className="mt-auto relative z-10">
                  <p className="text-sm opacity-90">
                    Already have an account?
                    <button onClick={handleToggleView} className="font-semibold underline ml-1">
                      Log In
                    </button>
                  </p>
                </div>
              </div>

              {/* White Panel - Signup */}
              <div
                className={`relative p-8 bg-white w-full sm:w-1/2 transition-all duration-700
                ${!isLoginView ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
              >
                <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-gray-800 text-3xl">
                  &times;
                </button>
                <h2 className="text-2xl font-aligran font-bold text-gray-900 mb-6 text-center">User Information</h2>

                {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
                {message && <div className="mb-3 text-sm text-green-600">{message}</div>}

                <form className="space-y-4" onSubmit={handleSignupSubmit}>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    className="w-full p-3 border border-gray-400 rounded-md placeholder-gray-600 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    value={signupMobile}
                    onChange={(e) => setSignupMobile(e.target.value)}
                    className="w-full p-3 border border-gray-400 rounded-md placeholder-gray-600 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="w-full p-3 border border-gray-400 rounded-md placeholder-gray-600 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">
                    Sign Up
                  </button>

                  <button
                    type="button"
                    onClick={() => openGoogleAuth(false)}
                    className="w-full flex items-center justify-center gap-2 border border-gray-400 py-2 px-4 rounded-md hover:bg-gray-100 transition text-gray-800 font-medium"
                  >
                    <FcGoogle size={22} /> Signup with Google
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPopup;
